import { useCallback, useEffect, useRef } from 'react';
import { getActiveSalonId, getActiveSalonToken } from './salonToken';
import config from '../entries/config';

/**
 * SSE calendario. Espone reconnect() per resume/refresh.
 * onerror: log + close (niente loop); la riapertura avviene al prossimo reconnect.
 */
export const useSSE = (callback, { pathIncludes = 'scheduler', enabled = true } = {}) => {
  const sourceRef = useRef(null);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const disconnect = useCallback(() => {
    if (!sourceRef.current) return;
    try {
      sourceRef.current.close();
    } catch (_) {
      /* ignore */
    }
    sourceRef.current = null;
  }, []);

  const connect = useCallback(() => {
    if (pathIncludes && !location.href.toString().includes(pathIncludes)) return;

    disconnect();

    const token = getActiveSalonToken();
    const id = getActiveSalonId();
    if (!id || !token) return;

    const url = config.sse_url + id;
    const eventsNewPrenotazione = new EventSource(url);
    sourceRef.current = eventsNewPrenotazione;

    eventsNewPrenotazione.onmessage = (event) => {
      if (!event || !event.data) return;
      try {
        const parsedData = JSON.parse(event.data);
        if (parsedData?.event && parsedData?.newEvent) {
          callbackRef.current?.(parsedData);
        }
      } catch (err) {
        console.log('SSE parse error', err);
      }
    };

    eventsNewPrenotazione.onerror = (event) => {
      console.log('Error on statusPrenotazioni', event);
      disconnect();
    };
  }, [disconnect, pathIncludes]);

  useEffect(() => {
    if (!enabled) {
      disconnect();
      return undefined;
    }

    const onVisibilityChange = () => {
      if (document.hidden) disconnect();
      else connect();
    };

    if (typeof document !== 'undefined' && document.hidden) {
      return () => disconnect();
    }

    connect();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  return { reconnect: connect, disconnect };
};
